/**
 * A faixa da contingência da SENATRAN (`R-06`, ADR-077): no topo de toda tela, para quem opera.
 *
 * Fora do ar, diz desde quando, quantos envios esperam e quanto falta para o mais antigo perder
 * as 24 h — o prazo não para —, quando o cockpit tenta de novo, e oferece o *Tentar agora*. Na
 * volta, por quinze minutos, diz que ela voltou e o que ainda sai da fila. Não se fecha: some
 * quando a SENATRAN responde e a fila sai.
 */
import { useState } from "react";
import { NavLink } from "react-router";

import { avisarQueAContingenciaPodeTerMudado } from "../api/contingencia";
import { ErroDaApi, api } from "../api/cliente";
import type { Contingencia, TentativaAgora } from "../api/tipos";
import { useAgora } from "../ganchos/useAgora";
import { textoDaFaixa } from "./contingencia";

const COR = {
  vermelho: "border-vermelho-borda bg-vermelho-fundo text-vermelho",
  amarelo: "border-amarelo-borda bg-amarelo-fundo text-amarelo",
  verde: "border-verde-borda bg-verde-fundo text-verde",
} as const;

export function FaixaDaContingencia({
  contingencia,
  aoTentar,
}: {
  contingencia: Contingencia | null;
  aoTentar: (depois: Contingencia) => void;
}) {
  const agora = useAgora(30_000, contingencia !== null);
  const [tentando, definirTentando] = useState(false);
  const [resultado, definirResultado] = useState<string | null>(null);
  const texto = textoDaFaixa(contingencia, agora);
  if (!texto) return null;

  async function tentar() {
    definirTentando(true);
    definirResultado(null);
    try {
      const r = await api<TentativaAgora>(
        "/tollvision/senatran/contingencia/tentar",
        {
          method: "POST",
          corpo: {},
        },
      );
      definirResultado(
        r.enviados > 0
          ? `${r.enviados === 1 ? "Saiu 1" : `Saíram ${r.enviados}`} da fila.`
          : r.recusados > 0
            ? `${r.recusados} recusado${r.recusados === 1 ? "" : "s"}: o motivo está na trilha.`
            : "Ainda sem resposta da SENATRAN.",
      );
      aoTentar(r.contingencia);
      avisarQueAContingenciaPodeTerMudado();
    } catch (falha) {
      definirResultado(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível tentar agora.",
      );
    } finally {
      definirTentando(false);
    }
  }

  return (
    <div
      role={texto.cor === "vermelho" ? "alert" : "status"}
      aria-label="Contingência da SENATRAN"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-cartao border px-3 py-2 text-[12px] ${COR[texto.cor]}`}
    >
      <span className="grid min-w-0 flex-1 basis-[280px] gap-0.5">
        <strong className="font-semibold">{texto.titulo}</strong>
        {texto.linhas.map((linha) => (
          <span key={linha} className="text-tinta-2">
            {linha}
          </span>
        ))}
        {resultado && (
          <span role="status" className="text-tinta-2">
            {resultado}
          </span>
        )}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        {texto.link && (
          <NavLink
            to={texto.link}
            className="rounded-botao border border-linha bg-painel-2 px-2.5 py-1 text-[11.5px] text-tinta-2 hover:bg-realce"
          >
            Ver a fila
          </NavLink>
        )}
        {texto.tentar && (
          <button
            type="button"
            disabled={tentando}
            onClick={() => void tentar()}
            className="rounded-botao border border-linha bg-painel-2 px-2.5 py-1 text-[11.5px] font-semibold text-tinta hover:bg-realce disabled:opacity-60"
          >
            {tentando ? "Tentando…" : "Tentar agora"}
          </button>
        )}
      </span>
    </div>
  );
}
